import { useCallback, useEffect, useRef, useState } from "react";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { Input } from "antd5";
import { ActiveSupplierTab } from "pages/app/SupplierSearchPage";
import useDeepCompareEffect from "use-deep-compare-effect";

import { ExportOrganisationsButton } from "components/organisations/ExportOrganisationsButton";
import Button from "components/ui/button/Button";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { createUseDebounce } from "lib/debounce";
import { parsePaginationUrlState, useURLState } from "lib/hooks/useURLState";
import { EventDataTypes } from "lib/tracking";
import { ExportOrgCountField } from "lib/types/graphQLEnums";
import { ColumnSetting } from "lib/types/models";
import { useTablePageSize } from "../../lib/hooks/useTablePageSize";
import FilterDrawer from "../filter_form/filter_drawer/FilterDrawer";
import { FilterForm, FilterFormProps } from "./FilterForm";
import { QuickFilterBar } from "./QuickFilterBar";
import { SaveSupplierButton } from "./SaveSupplierButton";
import { SupplierTable } from "./SupplierTable";
import { useSuppliersTracking } from "./tracking";
import {
  BASELINE_SUPPLIER_COLUMNS,
  convertSupplierFiltersToOrganisationParams,
  DEFAULT_FILTERS,
  parseSuppliersUrlState,
  SupplierColumns,
  SupplierFilters,
} from "./utils";

import css from "./FilterableSuppliers.module.scss";

export type SupplierSearchResultsInfo = {
  totalResults: number;
  isResultsLoading: boolean;
};

type Props = {
  defaultFilters?: SupplierFilters;
  defaultColumns?: ColumnSetting<SupplierColumns>[];
  allAvailableColumns?: ColumnSetting<SupplierColumns>[];
  hiddenFilterSections?: FilterFormProps["hiddenSections"];
  activeTab?: ActiveSupplierTab;
  setActiveTab?: (tab: ActiveSupplierTab) => void;
  /** Which list is showing, e.g. a profile sub-tab. Switching it starts the table on page 1. */
  view?: string;
};
export function FilterableSuppliers({
  defaultFilters = DEFAULT_FILTERS,
  defaultColumns = BASELINE_SUPPLIER_COLUMNS,
  allAvailableColumns = BASELINE_SUPPLIER_COLUMNS,
  hiddenFilterSections,
  activeTab,
  setActiveTab,
  view,
}: Props) {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const [filters, setFilters] = useURLState<SupplierFilters>(
    "supplierFilters",
    defaultFilters,
    parseSuppliersUrlState,
  );

  // Page and page size live in the URL, so Back and shared links show the same rows. Keyed per
  // table because profile tabs carry the URL's search params from one tab to the next.
  const [storedPageSize, setStoredPageSize] = useTablePageSize("suppliers-table");
  const [pagination = { page: 1, pageSize: storedPageSize }, setPagination] = useURLState(
    "suppliersPagination",
    { page: 1, pageSize: storedPageSize },
    (state) => parsePaginationUrlState(state, { page: 1, pageSize: storedPageSize }),
  );
  const currentPage = pagination.page ?? 1;
  const pageSize = pagination.pageSize ?? storedPageSize;
  const setCurrentPage = useCallback(
    (page: number) => setPagination((prev) => ({ ...prev, page })),
    [setPagination],
  );
  const setPageSize = useCallback(
    (size: number) => {
      setStoredPageSize(size);
      setPagination((prev) => ({ ...prev, pageSize: size }));
    },
    [setStoredPageSize, setPagination],
  );

  // A new view is a new list, so it starts on page 1. Keyed on the view rather than its filters,
  // which also change once as a view's supplier guids load, so a page kept in the URL survives.
  const previousView = useRef(view);
  useEffect(() => {
    if (previousView.current !== view) {
      previousView.current = view;
      setCurrentPage(1);
    }
  }, [view, setCurrentPage]);

  // If default supplier guids (switching supplier views or buyer header cards) are changed in a parent component,
  // then just change the guids and make sure buyers filter is persisted
  useDeepCompareEffect(() => {
    setFilters({
      ...filters,
      guids: defaultFilters.guids,
      buyerIds: defaultFilters.buyerIds,
    });
  }, [defaultFilters, setFilters]);

  const [supplierSearch, setSupplierSearch] = useState(filters.text);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const useDebounce = createUseDebounce(500);
  const debouncedSetFilters = useDebounce((f: SupplierFilters) => {
    setFilters(f);
    setSupplierSearch(f.text);
    setCurrentPage(1);
  });

  const tracking = useSuppliersTracking();
  const debouncedFreeSearchLog = useDebounce(() => tracking.freeSearchLog());

  const loggedFilterOpen = () => {
    setIsFilterDrawerOpen(true);
    tracking.filterDrawerOpened();
  };

  return (
    <div className={css.container}>
      <div className={css.actionsContainer}>
        <div className={css.actions}>
          <Input
            placeholder="Search by supplier name"
            prefix={<SearchOutlined />}
            value={supplierSearch}
            onChange={(e) => {
              setSupplierSearch(e.target.value);
              debouncedSetFilters({ ...filters, text: e.target.value });
              debouncedFreeSearchLog();
            }}
            allowClear
            className={css.searchInput}
          />
          <Button buttonType="secondary" onClick={loggedFilterOpen}>
            <FilterOutlined className={css.filterIcon} /> Filters
          </Button>
        </div>
        <div className={css.actions}>
          <div className={css.clearSelected}>
            {selectedSuppliers && selectedSuppliers.length > 0 && (
              <ClearSelectedButton
                count={selectedSuppliers.length}
                onClear={() => setSelectedSuppliers([])}
              />
            )}
          </div>
          {selectedSuppliers && selectedSuppliers.length === 0 && (
            <EditColumnsButton
              allAvailableColumns={allAvailableColumns}
              selectedColumns={selectedColumns}
              onNewColumnSettingChange={(c) =>
                setSelectedColumns([...(c as ColumnSetting<SupplierColumns>[])])
              }
              dataType={EventDataTypes.supplier}
            />
          )}
          <div className={css.saveSuppliers}>
            {selectedSuppliers.length > 0 && (
              <SaveSupplierButton
                supplierGuids={selectedSuppliers}
                trackingData={{ "Context source": "Bulk action" }}
              />
            )}
          </div>
          <ExportOrganisationsButton
            filters={convertSupplierFiltersToOrganisationParams(filters, pageSize, currentPage)}
            selectedOrganisationIds={selectedSuppliers}
            countFields={[ExportOrgCountField.AwardCount]}
          />
        </div>
      </div>
      <QuickFilterBar
        filters={filters}
        pageSize={pageSize}
        currentPage={currentPage}
        onClearFilter={(key) => debouncedSetFilters({ ...filters, [key]: undefined })}
        onFilterClick={() => setIsFilterDrawerOpen(true)}
        hiddenFilterSections={hiddenFilterSections}
        clearAllFilters={() => debouncedSetFilters(defaultFilters)}
      />
      <SupplierTable
        filters={filters}
        onSortChange={(sort) => {
          setFilters((filters: SupplierFilters) => ({
            ...filters,
            sort,
          }));
        }}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        currentPage={currentPage}
        selectedColumns={selectedColumns.map((f) => f.field)}
        selectedRows={selectedSuppliers}
        onSelectedRowsChange={setSelectedSuppliers}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
        {isFilterDrawerOpen && (
          <FilterForm
            onChange={debouncedSetFilters}
            filters={filters}
            closeDrawer={() => setIsFilterDrawerOpen(false)}
            hiddenSections={hiddenFilterSections}
          />
        )}
      </FilterDrawer>
    </div>
  );
}
